import React, {FC} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/mobile/lib/sendAnalyticsEvent/index';
import {Loader} from '@optimaxdev/design-system';
import {Icon, PaypalTransparent} from '@optimaxdev/icons';

import {PaymentMethodType} from '@primer-io/checkout-web';
import classNames from 'classnames';

import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';

import {ApplicationStoreType} from 'desktop/store';

import {STEPS} from '../../constants/checkout';
import {PAYMENTS} from '../../constants/paymentStep';
import {useGetErrorAndSendAnalytics} from '../../hooks/primer/useGetErrorAndSendAnalytics/useGetErrorAndSendAnalytics';
import {usePrimerNativeButton} from '../../hooks/primer/usePrimerNativeButton';
import {getCurrentStep} from '../../store/selectors/currentStep/currentStep';
import {getPaymentMethodSaveLoading} from '../../store/selectors/paymentStep/paymentMethodSave/paymentMethodSave';
import {getIsSelectedByKey} from '../../store/selectors/paymentStep/paymentStep';
import {PaymentStepAgreement} from '../paymentStepAgreement/paymentStepAgreement';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';

import {PaypalButtonFooter} from '../paypalButtonFooter/paypalButtonFooter';

import {primerPaypalOptions} from './constants';

import s from './paymentPaypalPrimer.scss';

export type PropsType = {
    isSelected: boolean;
    isLoading: boolean;
};

/**
 * Paypal with primer UI component
 */
const PaymentPaypalPrimer: FC<PropsType> = ({isLoading, isSelected}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {isAvailable, isReady} = usePrimerNativeButton({
        buttonId: primerPaypalOptions.id,
        buttonRenderOptions: isFacelift
            ? {
                  style: {buttonHeight: 48},
              }
            : {},
        externalMethodType: PaymentMethodType.PAYPAL,
        internalMethodType: PAYMENTS.METHOD_PRIMER_PAYPAL,
        onClick: () => {
            sendDesktopAnalyticsEvent(
                CHECKOUT_PAYMENT_STEP,
                {
                    message: 'CTA - Paypal Primer',
                    method: 'Payment',
                },
                true,
            );
        },
    });

    const {error} = useGetErrorAndSendAnalytics(PAYMENTS.METHOD_PRIMER_PAYPAL);

    return (
        <PaymentWrapperConnected
            className={classNames({
                [s.hide]: !isAvailable,
            })}
            title={
                <div className={classNames(s.title, {[s.titleFacelift]: isFacelift})}>
                    <Icon icon={PaypalTransparent} size="responsive" />
                </div>
            }
            code={PAYMENTS.METHOD_PRIMER_PAYPAL}
            isSelected={isSelected}
        >
            {!isReady && (
                <div aria-label="Loading paypal" className={s.loader}>
                    <Loader />
                </div>
            )}
            <div
                className={classNames({
                    [s.hide]: error,
                })}
            >
                {isReady && (
                    <p className={classNames(s.text, {[s.textFacelift]: isFacelift})}>
                        {`Click the button to be redirected to ${isFacelift ? 'PayPal' : 'PayPal.com'} & complete your purchase.`}
                        <PaymentStepAgreement
                            className={classNames(s.text, s.stepAgreementText, {
                                [s.stepAgreementTextFacelift]: isFacelift,
                            })}
                        />
                    </p>
                )}
                <div className={s.paypal} id={primerPaypalOptions.id} />
            </div>
            <PaypalButtonFooter error={String(error)} loading={isLoading} />
        </PaymentWrapperConnected>
    );
};

/**
 * Renders paypal primer method only in payment step
 */
export const PaymentPaypalPrimerContainer: FC<
    PropsType & {
        currentStep: STEPS;
    }
> = ({currentStep, ...props}) => {
    if (currentStep !== STEPS.PAYMENT_STEP) return null;

    return <PaymentPaypalPrimer {...props} />;
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    currentStep: getCurrentStep(store),
    isLoading: getPaymentMethodSaveLoading(store),
    isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_PRIMER_PAYPAL),
});

export const PaymentPaypalPrimerConnected = connect(mapStateToProps)(PaymentPaypalPrimerContainer);
